import NiceModal, { useModal } from "@ebay/nice-modal-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../redux/hooks";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../utils/marketplaceConstants";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, Wrap } from "../../../../styled-system/jsx";
import { center, vstack } from "../../../../styled-system/patterns";
import { formField } from "../../../../styled-system/recipes";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { Modal } from "../../../components/core/basic/Modal";
import { Datepicker } from "../../../components/fields/base/Datepicker";
import { NumberInput } from "../../../components/fields/base/NumberInput";
import { RadioGroup } from "../../../components/fields/base/RadioGroup";
import { Select } from "../../../components/fields/base/Select";
import { Button } from "../../../components/core/basic/Button";
import { formatCurrency } from "../../../utils/formatNumber";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { Label } from "react-aria-components";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

export interface BulkAdPlanActionFormData {
  actionType: string | null;
  actionValue?: number;
  actionUnit?: string;
  startDate?: string;
  endDate?: string;
  biddingProgram?: string;
  budgetProgram?: string;
  keepBoostedBid?: boolean;
}

interface Props {
  formData: BulkAdPlanActionFormData;
  setFormData: (data: BulkAdPlanActionFormData) => void;
  actionTarget: string;
  hidePercentOption?: boolean;
  hideCurrencyOption?: boolean;
  hideActionTypeOptions?: boolean;
  forcePercentOption?: boolean;
  forceCurrencyOption?: boolean;
  schedulingDialogue?: boolean;
  renderDateFields?: boolean;
  renderKeepBidBoostField?: boolean;
  disabledFields?: string[];
}

const BulkAdPlanActionForm = ({
  formData,
  setFormData,
  actionTarget,
  hidePercentOption,
  hideCurrencyOption,
  hideActionTypeOptions,
  forcePercentOption,
  forceCurrencyOption = false,
  schedulingDialogue,
  renderDateFields,
  disabledFields = [],
  renderKeepBidBoostField = false,
}: Props) => {
  const { t } = useTranslation(["advertising"]);
  const handleChange = <T extends keyof BulkAdPlanActionFormData>(key: T, value: BulkAdPlanActionFormData[T]) => {
    setFormData({ ...formData, [key]: value });
  };

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const classNames = formField();

  useEffect(() => {
    if (forcePercentOption) {
      setFormData({ ...formData, actionUnit: "percent" });
    }
    if (forceCurrencyOption) {
      setFormData({ ...formData, actionUnit: "currency" });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [forcePercentOption, forceCurrencyOption]);

  const actionTypeOptions = [
    { label: t(($) => $.advertising["Specify {actionTarget}"], { actionTarget }), value: "specify" },
    { label: t(($) => $.advertising["Increase {actionTarget}"], { actionTarget }), value: "increase" },
    { label: t(($) => $.advertising["Decrease {actionTarget}"], { actionTarget }), value: "decrease" },
  ];

  const { actionType, actionUnit, startDate, endDate } = formData;
  const normalizedActionTarget = actionTarget.toLowerCase().replace("acos", "ACOS").replace("roas", "ROAS");

  return (
    <form className={vstack({ alignItems: "stretch", width: "100%", paddingX: "xlarge" })}>
      <p className={css({ textAlign: "center" })}>
        {t(($) => $.advertising["Please specify changes to your {actionTarget} using the fields below."], {
          actionTarget: normalizedActionTarget,
        })}
      </p>
      <Wrap gap="xlarge" justify={"stretch"} width={"100%"}>
        {!hideActionTypeOptions && (
          <Select
            label={t(($) => $.advertising["Action Type"])}
            onSelectionChange={(value) => {
              handleChange("actionType", value as string);
            }}
            selectedKey={actionType}
            items={schedulingDialogue ? actionTypeOptions.filter((o) => o.value !== "specify") : actionTypeOptions}
            isDisabled={disabledFields.includes("actionType")}
            css={{
              flex: "2",
            }}
          />
        )}
        <NumberInput
          onChange={(value) => handleChange("actionValue", value)}
          value={formData.actionValue}
          label={t(($) => $.advertising["Amount"])}
          placeholder={t(($) => $.advertising["Enter amount"])}
          css={{
            flex: "2",
          }}
          isDisabled={disabledFields.includes("actionValue")}
          minValue={0}
        />
        {!(hideCurrencyOption && hidePercentOption) && (
          <RadioGroup
            className={css({
              flex: "1",
            })}
            orientation={"horizontal"}
            label={t(($) => $.advertising["Change Unit"])}
            isDisabled={disabledFields.includes("actionUnit")}
            items={[
              hideCurrencyOption ? null : (
                {
                  value: "currency",
                  label: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] ?? MARKETPLACE_CURRENCY_DENOMINATION["USA"]!,
                }
              ),
              hidePercentOption ? null : (
                {
                  value: "percent",
                  label: "%",
                }
              ),
            ].filter(Boolean)}
            value={actionUnit}
            onChange={(value) => handleChange("actionUnit", value)}
          />
        )}
      </Wrap>
      {(schedulingDialogue || renderDateFields) && (
        <Wrap gap="xlarge">
          <Datepicker
            label={
              <>
                {t(($) => $.advertising["Start Date"])}{" "}
                <HoverInfo>
                  {t(($) => $.advertising["Changes will occur at the beginning of the specified day"])}
                </HoverInfo>
              </>
            }
            value={startDate}
            onChange={(value) => handleChange("startDate", value as string)}
            disabled={disabledFields.includes("startDate")}
            minDate={moment().toDate()}
            css={{ flex: "2" }}
            inputClassName={css({ width: "100%" })}
            ignoreUtc={true}
          />
          <Datepicker
            label={
              <>
                {t(($) => $.advertising["End Date"])}{" "}
                <HoverInfo>{t(($) => $.advertising["Changes will occur at the end of the specified day"])}</HoverInfo>
              </>
            }
            value={endDate}
            onChange={(value) => handleChange("endDate", value as string)}
            disabled={disabledFields.includes("endDate")}
            minDate={moment(startDate).toDate()}
            css={{ flex: "2" }}
            inputClassName={css({ width: "100%" })}
            ignoreUtc={true}
          />
          {!(hideCurrencyOption && hidePercentOption) && <div className={css({ flex: "1" })} />}
          {renderKeepBidBoostField && (
            <Wrap justify={"flex-end"} align={"center"} paddingTop={"large"}>
              <Checkbox
                value={formData.keepBoostedBid ?? false}
                onChange={(value) => {
                  handleChange("keepBoostedBid", value);
                }}
              />
              <Label className={classNames.label}>
                <Flex gap={"small"}>
                  <span>
                    {actionTarget === "bid reduction" ?
                      t(($) => $.advertising["Keep Reduced Bid After End Date"])
                    : t(($) => $.advertising["Keep Boosted Bid After End Date"])}
                  </span>{" "}
                  <HoverInfo>
                    {actionTarget === "bid reduction" ?
                      t(
                        ($) =>
                          $.advertising[
                            "By default the bid will be reverted to it's value before the reduction period. Select this option to keep the bid value at the end of the bid reduction."
                          ],
                      )
                    : t(
                        ($) =>
                          $.advertising[
                            "By default the bid will be reverted to it's value before the boost period. Select this option to keep the bid value at the end of the bid boost."
                          ],
                      )
                    }
                  </HoverInfo>
                </Flex>
              </Label>
            </Wrap>
          )}
        </Wrap>
      )}
    </form>
  );
};

export default BulkAdPlanActionForm;

interface ModalProps extends Omit<
  Props,
  "formData" | "setFormData" | "hidePercentOption" | "hideCurrencyOption" | "forcePercentOption"
> {
  title?: string;
  initialValues?: BulkAdPlanActionFormData;
  onConfirm: (data: BulkAdPlanActionFormData) => void;
  actionTarget: string;
  unitAvailability?: "dynamic" | "alwaysPercent" | "none";
}

export const BulkAdPlanActionModal = NiceModal.create(
  ({
    title,
    unitAvailability = "dynamic",
    initialValues = {
      actionType: null,
      actionValue: 0,
      actionUnit: unitAvailability === "dynamic" ? "currency" : "percent",
      startDate: moment().toISOString().split("T")[0]!,
      endDate: moment().add(1, "day").toISOString().split("T")[0]!,
      keepBoostedBid: false,
    },
    onConfirm,
    ...props
  }: ModalProps) => {
    const { t } = useTranslation(["advertising", "common"]);
    const fmt = useDateFormatter("monthDay");
    const modal = useModal();
    const [formData, setFormData] = useState(initialValues);
    const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

    const isValid =
      !!formData.actionType &&
      formData.actionValue &&
      formData.actionValue > 0 &&
      !!formData.actionUnit &&
      ((!props.schedulingDialogue && !props.renderDateFields) || (!!formData.startDate && !!formData.endDate));

    const isDateValid = moment(formData.startDate).isSameOrBefore(moment(formData.endDate));

    const resolvedTitle = title ?? t(($) => $.advertising["Confirm Bulk Action"]);
    const normalizedActionTarget = props.actionTarget.toLowerCase().replace("acos", "ACOS").replace("roas", "ROAS");
    const formattedAmount =
      formData.actionUnit === "percent" ?
        `${Math.round(Math.abs(formData.actionValue as number))}%`
      : formatCurrency(Math.abs(formData.actionValue as number), { marketPlace });
    const formattedStartDate = fmt.format(moment(formData.startDate).toDate());
    const formattedEndDate = fmt.format(moment(formData.endDate).toDate());
    const hours = (moment(formData.endDate).diff(formData.startDate, "days") + 1) * 24;

    return (
      <Modal
        isOpen={modal.visible}
        isDismissable
        onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
        title={resolvedTitle}
        css={{ minWidth: "60vw" }}
      >
        <BulkAdPlanActionForm
          formData={formData}
          setFormData={setFormData}
          hidePercentOption={
            (unitAvailability === "dynamic" && formData.actionType === "specify") || unitAvailability === "none"
          }
          hideCurrencyOption={unitAvailability === "alwaysPercent" || unitAvailability === "none"}
          {...props}
        />
        {!isValid && (
          <p className={center({ margin: 0 })}>{t(($) => $.advertising["Please fill in required fields"])}</p>
        )}
        {props.schedulingDialogue && !isDateValid && (
          <p className={center({ margin: 0 })}>{t(($) => $.advertising["Start date must be before end date"])}</p>
        )}
        {isValid && props.schedulingDialogue && (
          <p className={center({ margin: 0, paddingX: "xlarge" })}>
            {formData.actionType === "increase" ?
              t(
                ($) =>
                  $.advertising[
                    "Are you sure you want to increase the {actionTarget} by {amount} for the selected media plans from {startDate} to {endDate} ({hours} hours)?"
                  ],
                {
                  actionTarget: normalizedActionTarget,
                  amount: formattedAmount,
                  startDate: formattedStartDate,
                  endDate: formattedEndDate,
                  hours,
                },
              )
            : formData.actionType === "decrease" ?
              t(
                ($) =>
                  $.advertising[
                    "Are you sure you want to decrease the {actionTarget} by {amount} for the selected media plans from {startDate} to {endDate} ({hours} hours)?"
                  ],
                {
                  actionTarget: normalizedActionTarget,
                  amount: formattedAmount,
                  startDate: formattedStartDate,
                  endDate: formattedEndDate,
                  hours,
                },
              )
            : t(
                ($) =>
                  $.advertising[
                    "Are you sure you want to specify the {actionTarget} by {amount} for the selected media plans from {startDate} to {endDate} ({hours} hours)?"
                  ],
                {
                  actionTarget: normalizedActionTarget,
                  amount: formattedAmount,
                  startDate: formattedStartDate,
                  endDate: formattedEndDate,
                  hours,
                },
              )
            }
          </p>
        )}
        <HStack justifyContent="space-around">
          <Button
            variant={"secondary"}
            onPress={() => {
              modal.remove();
            }}
          >
            {t(($) => $.common.actions["Cancel"])}
          </Button>

          <Button
            onPress={() => {
              onConfirm(formData);
              modal.remove();
            }}
            isDisabled={!isValid || !isDateValid}
          >
            {t(($) => $.advertising["Confirm"])}
          </Button>
        </HStack>
      </Modal>
    );
  },
);
